import {
  ExternalLink,
  ShieldAlert,
  UserRound,
  Landmark,
} from 'lucide-react';
import { STATUS_CONFIG, DEFAULT_STATUS_CONFIG, getIscStyle, getIscDescription, formatIscLevelName, formatStatusLabel, isEditable, isContentRemoved, PSR_CLASSIFICATION_URL } from '../constants/record';
import Tooltip from './Tooltip';

interface IscBadgeProps {
  level?: string;
  className?: string;
}

export function IscBadge({ level, className = '' }: IscBadgeProps) {
  return (
    <Tooltip
      interactive
      position="bottom"
      content={
        <span className="whitespace-pre-line">
          <span className="font-bold block mb-1">{formatIscLevelName(level)}</span>
          {getIscDescription(level)}
          <a
            href={PSR_CLASSIFICATION_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="block mt-1.5 text-emerald-400 hover:text-emerald-300 underline"
          >
            Learn more <ExternalLink className="w-3 h-3 inline ml-0.5" />
          </a>
        </span>
      }
    >
      <span className={`text-xs font-bold px-2 py-0.5 rounded-lg border flex items-center cursor-help ${getIscStyle(level)} ${className}`}>
        <ShieldAlert className="w-3 h-3 mr-1" />
        {formatIscLevelName(level)}
      </span>
    </Tooltip>
  );
}

interface PiiBadgeProps {
  detected?: boolean;
  level?: string;
  className?: string;
}

export function PiiBadge({ detected, level, className = '' }: PiiBadgeProps) {
  if (!detected || level?.toLowerCase() !== 'sensitive') return null;
  return (
    <Tooltip
      position="bottom"
      content="Names, contact info, and sensitive identifiers."
    >
      <span className={`text-xs font-bold px-2 py-0.5 rounded-lg border flex items-center cursor-help bg-red-100 text-red-700 border-red-300 ${className}`}>
        <UserRound className="w-3 h-3 mr-1" />
        Sensitive Personal Info
      </span>
    </Tooltip>
  );
}

interface MaoriDataBadgeProps {
  detected?: boolean;
  className?: string;
}

export function MaoriDataBadge({ detected, className = '' }: MaoriDataBadgeProps) {
  if (!detected) return null;
  return (
    <Tooltip
      position="bottom"
      content="Content related to Māori data, culture, or people."
    >
      <span className={`text-xs font-bold px-2 py-0.5 rounded-lg border flex items-center cursor-help bg-purple-100 text-purple-700 border-purple-300 ${className}`}>
        <Landmark className="w-3 h-3 mr-1" />
        Māori Data
      </span>
    </Tooltip>
  );
}

interface StatusBadgeProps {
  status?: string;
  immutabilityFinal?: boolean;
  className?: string;
}

export function StatusBadge({ status, immutabilityFinal, className = '' }: StatusBadgeProps) {
  const s = status?.toLowerCase() || '';
  const config = STATUS_CONFIG[s] ?? DEFAULT_STATUS_CONFIG;
  const Icon = config.icon;

  let tooltip: string;
  if (isEditable(immutabilityFinal)) {
    tooltip = 'Editable';
  } else if (isContentRemoved(status)) {
    tooltip = `Content removed`;
  } else {
    tooltip = `Not editable`;
  }

  return (
    <Tooltip content={tooltip} position="bottom">
      <span className={`text-xs font-bold px-2 py-0.5 rounded-lg border flex items-center cursor-help ${config.style} ${className}`}>
        <Icon className="w-3 h-3 mr-1" />
        {formatStatusLabel(status)}
      </span>
    </Tooltip>
  );
}
